/* HelpSupportButton — sidebar button + popover with primary tabs.
 *
 *   1. Bug      — quick-compose ticket pre-locked to type=bug.
 *   2. Feature  — quick-compose ticket pre-locked to type=feature.
 *   3. Roadmap  — preview of upcoming/shipped community ideas + link to /roadmap.
 *   4. Tickets  — open a new ticket + see your existing ones (live API).
 *   5. Docs     — static, searchable list of common articles (no API).
 *
 * Used in PortalShell's Account section across all 3 portals
 * (buyer / seller / admin). The Bug/Feature/Roadmap tabs replaced the
 * three standalone sidebar entries (Report a bug · Request a feature ·
 * Roadmap) in Feb 2026 to keep the sidebar uncluttered.
 *
 * Backend: /api/support/tickets, /api/roadmap.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HelpCircle, Search, BookOpen, Send, X, Check,
  ExternalLink, ArrowRight, Loader2, Inbox, ChevronLeft, AlertCircle,
  Bug, Lightbulb, Map, ArrowUp, AlertTriangle, Camera, Image as ImageIcon,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Help docs catalog — every entry covers a real platform feature with
// exact paths so the user can act on it immediately. Each doc declares its
// `audience` so the DocsTab can filter to what's relevant for the current
// portal (buyer / seller / both).
const DOCS = [
  // ── Getting started + identity (both portals) ─────────────────────────
  { id: "getting-started-buyer", audience: "buyer",
    title: "Getting started — connecting your first supplier",
    body: "Open Suppliers → Invite or accept an invite from your seller. Once connected, switch into their storefront from the supplier switcher (top-left). All orders, invoices and policies scope to that supplier automatically." },
  { id: "getting-started-seller", audience: "seller",
    title: "Getting started — your 5-step setup checklist",
    body: "When you first sign up, your portal is locked until 5 settings are filled: business location, default lead time + QI window, at least one warehouse address, default payment terms, and restocking-fee policy. Open Settings — the checklist banner at the top jumps you to each tab. Hit \"Complete setup & unlock portal\" when done." },
  { id: "kyb", audience: "both",
    title: "Verifying your business (KYB)",
    body: "Settings → Business details. Fill in legal name + EIN, upload your IRS letter, bank verification and articles of organization. Approval typically completes in 24h via Middesk. Sellers: KYB must be approved before verqo Capital will underwrite buyers ordering from you." },
  { id: "rbac", audience: "both",
    title: "Team roles — Owner / Admin / Operator / Viewer",
    body: "Invite teammates from Settings → Team members. Owner = full control. Admin = can move money + manage data, can't change banking or settings. Operator = day-to-day ops, no money. Viewer = read-only. Money endpoints (pay invoice, wallet top-up, multi-pay) and banking endpoints (cards/Plaid/payout schedule) are guarded server-side — non-Owners see a clear 'Permission required' message." },

  // ── verqo Score, credit, insurance, wallet (mainly buyer) ────────────
  { id: "score", audience: "buyer",
    title: "How Verqo Trade Score is calculated (5 signals)",
    body: "Your Verqo Trade Score (300–850 · BETA) blends payment history (35%), order frequency (25%), volume (20%), supplier diversity (10%) and shipping reliability (10%) — informational only, not a credit decision. Buyer can dispute or opt out anytime. See yours on /buyer/score." },
  { id: "credit", audience: "buyer",
    title: "Net-30 / Net-60 / Net-90 — paying with credit",
    body: "verqo Capital pays your invoice up-front; you repay us on terms. Net-30 is free. Net-60 and Net-90 add a small extension fee (0.10% × extra days). Apply from /buyer/payments → Credit. Decision in seconds via our AI underwriter (Verqo AI)." },
  { id: "insurance", audience: "buyer",
    title: "Trade-credit insurance — protect a single invoice",
    body: "Buyers can insure individual invoices from 0.10% of invoice value via Allianz Trade. Premium scales with your Verqo Trade Score (better score → cheaper coverage). Open Orders → select the invoice you want to protect → click 'Insure invoice' in the order drawer → Confirm coverage. Claim window: 7 days from delivery, evidence required." },
  { id: "wallet-topup", audience: "both",
    title: "Funding your verqo wallet",
    body: "Top up via ACH, wire or card. ACH typically settles in 1–2 business days; wires post same-day; card funds are available immediately. Buyers fund pre-orders + auto-pay. Sellers receive payouts here before they hit your bank." },
  { id: "wallet-activity", audience: "both",
    title: "Wallet activity & monthly statements",
    body: "Open /buyer/payments → Activity (or seller: /seller/wallet → Activity). The smart filter row lets you search by type, direction, method, date range and amount. Download monthly statements as PDF or CSV per month, or bulk-export with a date filter via the 'Export current view' button." },
  { id: "payment-methods", audience: "both",
    title: "Cards & bank accounts (ACH / Wire / IBAN)",
    body: "Settings → Payment & billing. Add up to 10 methods. Cards capture brand + last 4 (live detection: Visa / Mastercard / Amex / Discover). Banks capture routing + account + holder name + type + (for international) SWIFT/IBAN. Sensitive numbers are masked (••••1234) — tap Reveal and enter your password to expose them for 30 seconds. Card billing address and an authorization checkbox are required for fraud protection." },

  // ── Orders, POs, prebook, SLAs ────────────────────────────────────────
  { id: "po-flow", audience: "buyer",
    title: "Placing a PO — verqo / Seller / Buyer-arranged shipping",
    body: "Catalog or PO Generator → Confirm PO. Pick a shipping mode: verqo (managed by us, insured), Seller-arranged (only when the seller has opted in), or Buyer-arranged (you handle pickup — requires a waiver, transit-damage claims auto-blocked). You can change shipping mode later via the order drawer, until the order ships." },
  { id: "order-docs", audience: "both",
    title: "Downloading PO / Invoice / Pack List",
    body: "Open any order (Orders → click row). The drawer has three download buttons: PO (a branded purchase-order PDF), Invoice (PDF + CSV — sellers also get the inline cell in the Orders table), and Pack List (available once shipping info is entered). Every PDF stamps Invoice # · Order # · Buyer PO # at the top so AP/AR can reconcile in one glance." },
  { id: "order-sla", audience: "seller",
    title: "Order SLAs — extension requests & overdue handling",
    body: "Every order has a committed ship-by date (your default lead time, or the date you typed when confirming). Need more time? Open the order drawer → Request extension. You get 3 extensions per order; the buyer can accept/reject (10-day silence auto-accepts). If you miss the date and never request an extension, the order can be auto-cancelled + your wallet set-off + claim filed." },
  { id: "prebook", audience: "seller",
    title: "Prebook orders (deposit + balance) — for made-to-order goods",
    body: "Enable in Settings → Public profile → Logistics → Offer prebook. On any pending PO, click 'Convert to prebook' in the drawer, set deposit % (typically 30%) + lead-time weeks. The buyer pays the deposit immediately; balance settles when you mark ready_for_pickup. You can request up to 3 extensions; the buyer can cancel only after the promised date passes." },
  { id: "prebook-buyer", audience: "buyer",
    title: "What is a prebook offer?",
    body: "Some sellers offer made-to-order goods that don't ship from stock. When you receive a 'Prebook offer' in your orders, accept it to pay a partial deposit now (typically 30%); the balance auto-settles when the goods are ready. If the seller misses the promised date, you can cancel and we'll process a refund (instant from their wallet, or escalated to a claim if they're out of funds)." },
  { id: "cancel-refund", audience: "buyer",
    title: "Cancelling an order — restocking fee policy",
    body: "Order drawer → Cancel order (hidden once shipped). Cancellation before packing = full refund. Cancellation after the seller has packed = restocking fee (the seller's published % on their public profile, capped at 25%). The fee + refund math + an acknowledgement modal show before you confirm. Pickup window: orders left uncollected for 30 days auto-cancel with the restocking fee applied." },
  { id: "auto-cancel", audience: "buyer",
    title: "30-day auto-cancel for unpaid invoices",
    body: "Once a seller confirms your PO, the invoice is due within 30 calendar days. Day 10 → gentle reminder. Day 20 → firm reminder + SMS. Day 30 → final notice + the order auto-cancels + invoice is voided. Top up your wallet or pay via card any time before Day 30 to keep the order live." },

  // ── Seller-side ops & growth ──────────────────────────────────────────
  { id: "seller-policies", audience: "seller",
    title: "Where your published policies appear on buyer-facing docs",
    body: "Settings → Public profile → MOQ, warranty, ships-from/to, restocking fee, prebook toggle, dropship toggle. Every invoice + pack list PDF/CSV you generate auto-stamps these as 'Terms & Policies' chips so the buyer's AP team always sees them." },
  { id: "shipping-live-rates", audience: "seller",
    title: "Shipping: live carrier rates",
    body: "On any order, open the drawer → Enter shipping info. Pick parcel mode and enter dimensions to fetch live rates from USPS, UPS, FedEx and DHL. Buy a label in one click — the PDF and tracking link are saved on the shipment. For LTL/freight, enter pallet count and the system flips the booking type automatically." },
  { id: "buyer-arranged-release", audience: "seller",
    title: "Buyer-arranged shipping — issuing a release receipt",
    body: "When a buyer picks 'I'll arrange shipping' on their PO, you don't book a carrier — you wait for them to pick up. Once they arrive, open the order drawer → 'Release to buyer'. Capture recipient name, optional phone, up to 4 photos, signature acknowledgement. Issuing the receipt flips status to delivered and transfers transit risk to the buyer." },
  { id: "domain-portal", audience: "seller",
    title: "Custom domain + portal launch kit",
    body: "Settings → Domain. We give you a permanent verqo subdomain. To use your own (e.g. shop.brand.com), add it under Custom domain — we register it with Cloudflare for SaaS, you add a CNAME record at your DNS provider. Once active, your portal URL auto-updates. The Launch Kit (same page) gives you a copy-able link, QR code, HTML banner, floating widget snippet, and email-signature block to drop into your existing buyer touch points." },
  { id: "dropship-3id", audience: "seller",
    title: "Drop-shipping: how 3-ID reconciliation works",
    body: "Each dropship order carries 3 IDs: the buyer's Shopify order ID, the verqo code, and your Shopify order ID. We auto-link them so your customer email always carries real tracking. Confirm shipments before 11 AM EST daily; an invoice is auto-generated at midnight for every buyer with shipped orders that day. Returns + damage policies live in /seller/dropship → Buyer settings → Agreement." },
  { id: "apps-install", audience: "seller",
    title: "Installing apps (Dropship, RFQ, etc.) — even with zero buyers",
    body: "Apps → click Install. PER_CONNECTION apps (Dropship, RFQ) show a scope picker — 'All buyers (current + future)' applies the app to every buyer that ever connects, so you can install even before your first buyer connects. To change scope later, click 'edit' on the installed app card. Apps marked 'Coming soon' (EDI, Shopify Connector, ERP/WMS, QuickBooks for sellers) aren't installable yet — hit 'Notify me' to be alerted at launch." },
  { id: "apps-install-buyer", audience: "buyer",
    title: "Installing apps in the App Store",
    body: "Buyer → Apps. Global apps (PO Generator, AI Assistant, Inventory) install in one click. PER_CONNECTION apps (Dropship, RFQ) need at least one connected supplier — invite/accept first. Apps marked 'Coming soon' aren't installable yet — hit 'Notify me'." },

  // ── Compliance + legal (both) ─────────────────────────────────────────
  { id: "legal-consent", audience: "both",
    title: "Cookies + privacy preferences",
    body: "We use strictly-necessary cookies for sign-in + session. Functional/Analytics/Marketing cookies are off by default; you can re-open the preferences from the footer 'Cookies' link anytime. CCPA: visit /legal/do-not-sell to opt out of any cross-context behavioral advertising (we don't do any today)." },
  { id: "help-tickets", audience: "both",
    title: "Filing a support ticket (bug / feature / question / billing)",
    body: "Help & Support (this panel) → Bug or Feature tab. Add a subject, description and urgency. We respond on business hours within 1 business day for normal urgency, faster for 'Money is stuck' or 'Production down'. Track all your open + resolved tickets under the Tickets tab. Errors anywhere in the portal can auto-prefill a bug ticket via the 'Open ticket' button on the error toast." },
];

const URGENCY = [
  { v: "low",     l: "Low — when you can",          color: "bg-secondary text-foreground/70 border-border" },
  { v: "normal",  l: "Normal — within a day",       color: "bg-secondary text-foreground/70 border-border" },
  { v: "high",    l: "High — money is stuck",       color: "bg-amber-50 text-amber-800 border-amber-200" },
  { v: "urgent",  l: "Urgent — production down",    color: "bg-rose-50 text-rose-800 border-rose-200" },
];

const TYPES = [
  { v: "bug",      l: "Bug",                emoji: "🐞", desc: "Something's broken" },
  { v: "feature",  l: "Feature request",    emoji: "💡", desc: "Suggest something new" },
  { v: "question", l: "Question",           emoji: "❓", desc: "Need help understanding" },
  { v: "billing",  l: "Billing",            emoji: "💳", desc: "Pricing, invoices, refunds" },
];

const STATUS_BADGE = {
  open:          { label: "Open",         color: "bg-amber-50 text-amber-800 border-amber-200" },
  in_progress:   { label: "In progress",  color: "bg-blue-50 text-blue-700 border-blue-200" },
  waiting_user:  { label: "Reply needed", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  resolved:      { label: "Resolved",     color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  closed:        { label: "Closed",       color: "bg-secondary text-foreground/55 border-border" },
};

export default function HelpSupportButton() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("stuck"); // stuck | bug | feature | roadmap | tickets | docs
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);  // true after first fetch resolves
  const [activeTicket, setActiveTicket] = useState(null); // ticket id when reading
  const [prefill, setPrefill] = useState(null);  // {subject, body, urgency, category}
  const popRef = useRef(null);

  const loadTickets = () => {
    setLoading(true);
    api.get("/support/tickets")
      .then(r => setTickets(r.data?.items || []))
      .catch(() => setTickets([]))
      .finally(() => { setLoading(false); setLoaded(true); });
  };

  useEffect(() => {
    if (open && tab === "tickets" && !activeTicket) loadTickets();
  }, [open, tab, activeTicket]);

  // Listen for "open-help-ticket" events fired from anywhere in the app
  // (e.g. error toasts with an "Open ticket" action). Force-open the
  // popover, route to the matching primary tab (bug/feature) when known,
  // and pre-fill the compose form.
  useEffect(() => {
    const onOpenTicket = (e) => {
      const detail = e?.detail || {};
      setPrefill({
        subject: detail.subject || "",
        body: detail.body || "",
        urgency: detail.urgency || "high",
        type: detail.type || "",
        category: detail.category || "",
      });
      setActiveTicket(null);
      // Route to the matching quick tab when the caller specified a type;
      // otherwise drop into the generic Tickets tab so the user can pick.
      if (detail.type === "bug") setTab("bug");
      else if (detail.type === "feature") setTab("feature");
      else setTab("tickets");
      setOpen(true);
    };
    window.addEventListener("open-help-ticket", onOpenTicket);
    return () => window.removeEventListener("open-help-ticket", onOpenTicket);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) {
        // Don't close when clicking the trigger button itself.
        const trigger = document.querySelector('[data-testid="sidebar-help-btn"]');
        if (trigger && trigger.contains(e.target)) return;
        setOpen(false);
        setPrefill(null);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); setPrefill(null); } };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // ── Dynamic popover positioning ────────────────────────────────────────
  // Anchor the popover to the trigger button's actual viewport coordinates
  // instead of the previous hardcoded `left-[260px] bottom-6`.
  //
  // Why this matters: the old hardcoded offset assumed an exact 260px
  // sidebar width. On narrower laptops where the sidebar collapses or on
  // mobile where it hides entirely, the popover ended up orphaned in the
  // middle of the page, partly off-screen, or overlapping the content
  // area. Reported as "not overlaying / not fully visible" — Feb 2026.
  //
  // We now:
  //  - Read the trigger button's actual right edge + bottom on every
  //    open + on resize, so the popover follows the sidebar's real width.
  //  - Clamp the popover's top to >= 16px so it never overflows above the
  //    viewport on shorter screens.
  //  - On viewports < 768px (no sidebar), fall back to a centered bottom
  //    sheet that takes the full viewport width with safe insets.
  const triggerRef = useRef(null);
  const [popPos, setPopPos] = useState(null);

  useEffect(() => {
    if (!open) return;
    const recompute = () => {
      const trigger = triggerRef.current
        || document.querySelector('[data-testid="sidebar-help-btn"]');
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // Mobile / narrow → centered bottom sheet
      if (vw < 768) {
        setPopPos({
          left: 12,
          right: 12,
          bottom: 12,
          maxHeight: Math.min(vh - 24, 640),
          width: vw - 24,
          mobile: true,
        });
        return;
      }
      // Desktop / tablet → anchor right of the trigger button (with fallback)
      const tBox = trigger ? trigger.getBoundingClientRect() : null;
      const sidebarRight = tBox ? tBox.right : 260;  // 260px is the original fallback
      const desiredLeft = sidebarRight + 12;
      const popoverWidth = 440;
      // If anchoring right of the sidebar overflows, push the popover left
      // so it always fits inside the viewport with a 12px margin.
      const left = Math.min(desiredLeft, vw - popoverWidth - 12);
      // Bottom anchor — keep ~24px from the viewport floor, but ensure the
      // popover doesn't overflow above the viewport on short screens.
      const maxHeight = Math.min(vh - 32, 640);  // 32px = 16 top + 16 bottom min safe
      setPopPos({
        left: Math.max(12, left),
        bottom: 24,
        width: popoverWidth,
        maxHeight,
        mobile: false,
      });
    };
    recompute();
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => { setOpen(v => !v); if (open) setPrefill(null); }}
        data-testid="sidebar-help-btn"
        className={`w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${open ? "bg-foreground text-background" : "text-foreground/70 hover:bg-secondary hover:text-foreground"}`}
      >
        <HelpCircle className="w-4 h-4" />
        <span className="flex-1 text-left">Help & Support</span>
      </button>

      {open && (
        <>
          {/* Subtle backdrop — makes it obvious the popover is sitting on
              top of whatever drawer/page is underneath, and gives the user
              a click-anywhere-to-dismiss target. */}
          <div
            data-testid="help-support-backdrop"
            onClick={() => { setOpen(false); setActiveTicket(null); setPrefill(null); }}
            className="fixed inset-0 z-[105] bg-black/15 backdrop-blur-[1px]"
          />
          <div
            ref={popRef}
            data-testid="help-support-popover"
            className="fixed z-[110] rounded-2xl border border-border bg-white shadow-2xl overflow-hidden flex flex-col"
            style={popPos ? {
              // Mobile bottom-sheet — full-width with safe insets.
              ...(popPos.mobile ? {
                left:   popPos.left,
                right:  popPos.right,
                bottom: popPos.bottom,
                maxHeight: popPos.maxHeight,
              } : {
                // Desktop side popover — viewport-clamped anchor next to sidebar.
                left:   popPos.left,
                bottom: popPos.bottom,
                width:  popPos.width,
                maxHeight: popPos.maxHeight,
              }),
            } : {
              // Pre-mount fallback — render off-screen for the first frame
              // so we don't flash at the wrong position before recompute().
              left:   -9999,
              bottom: 24,
              width:  440,
              maxHeight: 640,
              visibility: "hidden",
            }}
          >
          {/* Header */}
          <div className="px-4 py-3 border-b border-border flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              {activeTicket ? (
                <button
                  onClick={() => setActiveTicket(null)}
                  data-testid="help-back-btn"
                  className="text-foreground/55 hover:text-foreground"
                >
                  <ChevronLeft className="w-4 h-4"/>
                </button>
              ) : (
                <HelpCircle className="w-4 h-4 text-foreground/70"/>
              )}
              <div className="font-medium text-sm">
                {activeTicket ? "Ticket detail" : "How can we help?"}
              </div>
            </div>
            <button
              onClick={() => { setOpen(false); setActiveTicket(null); setPrefill(null); }}
              data-testid="help-close-btn"
              className="text-foreground/45 hover:text-foreground"
            >
              <X className="w-4 h-4"/>
            </button>
          </div>

          {!activeTicket && (
            <div className="px-3 pt-3 shrink-0">
              <div className="inline-flex rounded-full bg-secondary/50 p-0.5 text-[11px] gap-0.5 flex-wrap">
                {[
                  { k: "stuck",   l: "I'm stuck", icon: AlertTriangle },
                  { k: "bug",     l: "Bug",     icon: Bug },
                  { k: "feature", l: "Feature", icon: Lightbulb },
                  { k: "roadmap", l: "Roadmap", icon: Map },
                  { k: "tickets", l: "Tickets", icon: Inbox },
                  { k: "docs",    l: "Docs",    icon: BookOpen },
                ].map(t => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.k}
                      onClick={() => { setTab(t.k); setPrefill(null); }}
                      data-testid={`help-tab-${t.k}`}
                      className={`px-2.5 py-1.5 rounded-full transition-colors inline-flex items-center gap-1 ${tab === t.k ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}
                    >
                      <Icon className="w-3 h-3"/>
                      {t.l}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-4 text-sm">
            {activeTicket ? (
              <TicketDetail
                ticketId={activeTicket}
                onClosed={() => { setActiveTicket(null); loadTickets(); }}
              />
            ) : tab === "docs" ? (
              <DocsTab/>
            ) : tab === "stuck" ? (
              <StuckTab onCreated={() => { setTab("tickets"); loadTickets(); }}/>
            ) : tab === "roadmap" ? (
              <RoadmapTab onCompose={() => { setTab("feature"); }}/>
            ) : tab === "bug" ? (
              <QuickComposeTab
                lockedType="bug"
                prefill={prefill}
                onPrefillConsumed={() => setPrefill(null)}
                onCreated={() => { setTab("tickets"); loadTickets(); }}
              />
            ) : tab === "feature" ? (
              <QuickComposeTab
                lockedType="feature"
                prefill={prefill}
                onPrefillConsumed={() => setPrefill(null)}
                onCreated={() => { setTab("tickets"); loadTickets(); }}
              />
            ) : (
              <TicketsTab
                tickets={tickets}
                loading={loading}
                loaded={loaded}
                prefill={prefill}
                onPrefillConsumed={() => setPrefill(null)}
                onCreated={() => loadTickets()}
                onOpenTicket={(tid) => setActiveTicket(tid)}
              />
            )}
          </div>
        </div>
        </>
      )}
    </>
  );
}

/* ─── Docs tab ──────────────────────────────────────────────────────────── */
function DocsTab() {
  const [q, setQ] = useState("");
  // Filter the catalog by the active portal — buyers only see buyer + both
  // docs, sellers only see seller + both. Admins see everything.
  const { user } = useAuth();
  const role = user?.role || "buyer";
  const roleScopedDocs = useMemo(() => {
    if (role === "admin") return DOCS;
    return DOCS.filter(d => d.audience === "both" || d.audience === role);
  }, [role]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return roleScopedDocs;
    return roleScopedDocs.filter(d =>
      d.title.toLowerCase().includes(needle) || d.body.toLowerCase().includes(needle)
    );
  }, [q, roleScopedDocs]);
  const [openId, setOpenId] = useState(null);

  return (
    <div className="space-y-3" data-testid="help-docs-tab">
      <div className="relative">
        <Search className="w-3.5 h-3.5 text-foreground/40 absolute top-1/2 -translate-y-1/2 left-3"/>
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder={role === "seller"
            ? "Search seller docs (e.g. prebook, dropship, payouts)"
            : role === "buyer"
              ? "Search buyer docs (e.g. credit, insurance, cancel)"
              : "Search the docs"}
          data-testid="help-docs-search"
          className="w-full h-10 rounded-lg border border-border bg-secondary/30 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/10"
        />
      </div>
      <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/45">
        {filtered.length} article{filtered.length === 1 ? "" : "s"} · {role === "admin" ? "all portals" : role + " portal"}
      </div>
      {filtered.length === 0 ? (
        <div className="text-center py-6 text-foreground/55 text-xs">
          No docs match "{q}". Try the Tickets tab to ask us directly.
        </div>
      ) : (
        <div className="space-y-1">
          {filtered.map(d => (
            <div key={d.id}>
              <button
                onClick={() => setOpenId(openId === d.id ? null : d.id)}
                data-testid={`help-doc-${d.id}`}
                className="flex w-full items-center justify-between rounded-lg px-3 py-2 hover:bg-secondary/40 text-left text-[13px]"
              >
                <span className="flex-1 pr-2 truncate">
                  <BookOpen className="w-3 h-3 inline mr-1.5 text-foreground/45"/>
                  {d.title}
                  {d.audience === "both" && (
                    <span className="ml-1.5 text-[9px] mono uppercase tracking-[0.14em] text-foreground/40 align-middle">· both</span>
                  )}
                </span>
                <ExternalLink className="w-3 h-3 text-foreground/30 shrink-0"/>
              </button>
              {openId === d.id && (
                <div className="px-3 pt-1 pb-3 text-[12.5px] text-foreground/70 leading-relaxed">
                  {d.body}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Tickets tab ───────────────────────────────────────────────────────── */
function TicketsTab({ tickets, loading, loaded, prefill, onPrefillConsumed, onCreated, onOpenTicket }) {
  const [composing, setComposing] = useState(false);
  // Default to compose mode the FIRST time the tab opens with no tickets
  // (and only AFTER the initial fetch has resolved). Respect the user's
  // explicit toggle on subsequent renders.
  const [autoSet, setAutoSet] = useState(false);
  useEffect(() => {
    if (loaded && !autoSet) {
      if (tickets.length === 0) setComposing(true);
      setAutoSet(true);
    }
  }, [loaded, tickets.length, autoSet]);

  // Prefill arrived from an external "Open ticket" CTA — force compose mode.
  useEffect(() => {
    if (prefill) setComposing(true);
  }, [prefill]);

  if (!loaded && loading) {
    return (
      <div className="text-center py-6 text-foreground/55 text-xs inline-flex items-center justify-center gap-2 w-full">
        <Loader2 className="w-3.5 h-3.5 animate-spin"/>Loading your tickets…
      </div>
    );
  }

  return composing ? (
    <NewTicketForm
      prefill={prefill}
      onPrefillConsumed={onPrefillConsumed}
      onCancel={tickets.length > 0 ? () => setComposing(false) : null}
      onCreated={() => { setComposing(false); onCreated(); }}
    />
  ) : (
    <div className="space-y-3" data-testid="help-tickets-tab">
      <button
        onClick={() => setComposing(true)}
        data-testid="help-new-ticket-btn"
        className="w-full h-10 rounded-full bg-foreground text-background hover:bg-foreground/90 text-sm font-medium inline-flex items-center justify-center gap-2"
      >
        <Send className="w-3.5 h-3.5"/>Open a new ticket
      </button>
      {loading ? (
        <div className="text-center py-6 text-foreground/55 text-xs inline-flex items-center justify-center gap-2 w-full">
          <Loader2 className="w-3.5 h-3.5 animate-spin"/>Loading your tickets…
        </div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-8 text-foreground/55 text-xs">
          <Inbox className="w-7 h-7 mx-auto text-foreground/30 mb-2"/>
          No tickets yet — click above to open one.
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="help-tickets-list">
          {tickets.map(t => {
            const badge = STATUS_BADGE[t.status] || STATUS_BADGE.open;
            return (
              <li key={t.id}>
                <button
                  onClick={() => onOpenTicket(t.id)}
                  data-testid={`help-ticket-row-${t.number}`}
                  className="w-full text-left rounded-xl border border-border p-3 hover:bg-secondary/40 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] mono uppercase tracking-widest text-foreground/55">
                      #{t.number}
                    </span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${badge.color}`}>
                      {badge.label}
                    </span>
                  </div>
                  <div className="mt-1 font-medium text-[13px] truncate">{t.subject}</div>
                  <div className="mt-0.5 text-[11px] text-foreground/55 truncate">
                    {t.messages?.length || 1} message{(t.messages?.length || 1) === 1 ? "" : "s"}
                    {" · "}
                    {new Date(t.updated_at || t.created_at).toLocaleDateString()}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function NewTicketForm({ prefill, onPrefillConsumed, onCancel, onCreated, lockedType }) {
  const [form, setForm] = useState({
    subject: prefill?.subject || "",
    body: prefill?.body || "",
    urgency: prefill?.urgency || (lockedType === "bug" ? "high" : "normal"),
    type: lockedType || prefill?.type || "question",
    category: prefill?.category || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);
  const [hasPrefill] = useState(!!prefill?.subject);

  // If a NEW prefill arrives while the form is open (rare — user opens the
  // popover, then triggers another error), refresh the fields.
  useEffect(() => {
    if (prefill?.subject || prefill?.type) {
      setForm((f) => ({
        subject: prefill.subject || f.subject,
        body: prefill.body || f.body,
        urgency: prefill.urgency || f.urgency || "high",
        type: prefill.type || f.type || "question",
        category: prefill.category || f.category,
      }));
    }
  }, [prefill?.subject, prefill?.body, prefill?.urgency, prefill?.type, prefill?.category]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.category) delete payload.category;
      const res = await api.post("/support/tickets", payload);
      setCreated(res.data);
      if (onPrefillConsumed) onPrefillConsumed();
      setTimeout(() => onCreated && onCreated(), 1800);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to open ticket";
      setError(typeof msg === "string" ? msg : "Failed to open ticket");
    } finally {
      setSubmitting(false);
    }
  };

  if (created) {
    return (
      <div className="text-center py-6" data-testid="help-ticket-sent">
        <div className="w-12 h-12 rounded-full bg-emerald-100 grid place-items-center mx-auto">
          <Check className="w-5 h-5 text-emerald-700"/>
        </div>
        <div className="font-medium mt-3">Ticket #{created.number} opened</div>
        <div className="text-xs text-foreground/55 mt-1">
          We'll email you at {created.user_email}.
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-2.5" data-testid="help-new-ticket-form">
      {hasPrefill && (
        <div
          data-testid="help-ticket-prefill-banner"
          className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-[12px] px-3 py-2 flex items-start gap-2"
        >
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
          <span>
            Auto-filled from the error you just saw. Add anything else helpful, or send as-is.
          </span>
        </div>
      )}
      {/* Type selector — bug / feature / question / billing.
          Hidden when the parent tab locks the type (Bug/Feature quick-compose). */}
      {!lockedType && (
      <div className="grid grid-cols-4 gap-1.5">
        {TYPES.map(t => (
          <button
            key={t.v}
            type="button"
            onClick={() => setForm({ ...form, type: t.v })}
            data-testid={`help-ticket-type-${t.v}`}
            title={t.desc}
            className={`px-2 py-1.5 rounded-lg border text-left transition-colors ${form.type === t.v ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground/40 bg-white"}`}
          >
            <div className="text-base leading-none">{t.emoji}</div>
            <div className={`text-[10.5px] mt-0.5 truncate ${form.type === t.v ? "text-background" : "text-foreground/70"}`}>{t.l}</div>
          </button>
        ))}
      </div>
      )}
      <input
        value={form.subject}
        onChange={e => setForm({ ...form, subject: e.target.value })}
        placeholder={form.type === "bug" ? "What broke?" : form.type === "feature" ? "What should verqo do?" : "What's going on?"}
        required
        minLength={2}
        maxLength={200}
        data-testid="help-ticket-subject"
        className="w-full h-10 rounded-lg border border-border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/10"
      />
      <textarea
        value={form.body}
        onChange={e => setForm({ ...form, body: e.target.value })}
        placeholder={form.type === "feature" ? "Describe the use case + the outcome you'd love" : "Add any details — order number, error message, what you were trying to do…"}
        required
        minLength={2}
        maxLength={8000}
        rows={5}
        data-testid="help-ticket-body"
        className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/10"
      />
      {form.type !== "feature" && (
        <select
          value={form.urgency}
          onChange={e => setForm({ ...form, urgency: e.target.value })}
          data-testid="help-ticket-urgency"
          className="w-full h-10 rounded-lg border border-border px-3 text-sm bg-white"
        >
          {URGENCY.map(u => (<option key={u.v} value={u.v}>{u.l}</option>))}
        </select>
      )}
      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-800 text-[12px] p-2.5 inline-flex items-start gap-2 w-full">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
          <span>{error}</span>
        </div>
      )}
      <div className="flex gap-2 pt-1">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            data-testid="help-ticket-cancel"
            className="flex-1 h-10 rounded-full border border-border hover:bg-secondary text-sm"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={submitting}
          data-testid="help-ticket-send"
          className="flex-1 h-10 rounded-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60 text-sm font-medium inline-flex items-center justify-center gap-2"
        >
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>}
          {submitting ? "Sending…" : "Open ticket"}
        </button>
      </div>
    </form>
  );
}

/* ─── Ticket detail (read + reply) ──────────────────────────────────────── */
function TicketDetail({ ticketId, onClosed }) {
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [closing, setClosing] = useState(false);

  const load = () => {
    api.get(`/support/tickets/${ticketId}`)
      .then(r => setTicket(r.data))
      .catch(err => setError(err?.response?.data?.detail || "Failed to load ticket"));
  };
  useEffect(load, [ticketId]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true); setError(null);
    try {
      await api.post(`/support/tickets/${ticketId}/messages`, { body: reply.trim() });
      setReply("");
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const closeTicket = async () => {
    setClosing(true);
    try {
      await api.patch(`/support/tickets/${ticketId}/status`, { status: "closed" });
      onClosed && onClosed();
    } catch {
      /* swallow */
    } finally {
      setClosing(false);
    }
  };

  if (!ticket && !error) {
    return (
      <div className="text-center py-6 text-foreground/55 text-xs inline-flex items-center justify-center gap-2 w-full">
        <Loader2 className="w-3.5 h-3.5 animate-spin"/>Loading…
      </div>
    );
  }
  if (error && !ticket) {
    return <div className="text-rose-700 text-xs">{error}</div>;
  }

  const badge = STATUS_BADGE[ticket.status] || STATUS_BADGE.open;
  const isClosed = ticket.status === "closed";

  return (
    <div className="space-y-3" data-testid={`help-ticket-detail-${ticket.number}`}>
      <div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] mono uppercase tracking-widest text-foreground/55">#{ticket.number}</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${badge.color}`}>{badge.label}</span>
        </div>
        <div className="mt-1 font-medium text-sm">{ticket.subject}</div>
        <div className="mt-0.5 text-[11px] text-foreground/55">
          Urgency: {ticket.urgency} · Opened {new Date(ticket.created_at).toLocaleString()}
        </div>
      </div>

      <ul className="space-y-2" data-testid="help-ticket-messages">
        {(ticket.messages || []).map(m => (
          <li
            key={m.id}
            className={`rounded-xl p-3 text-[12.5px] ${m.author_role === "admin" ? "bg-secondary/60 border border-border" : "bg-foreground text-background"}`}
          >
            <div className={`text-[10px] mono uppercase tracking-widest mb-1 ${m.author_role === "admin" ? "text-foreground/55" : "text-accent/80"}`}>
              {m.author_role === "admin" ? "verqo support" : (m.author_name || "You")}
              {" · "}
              {new Date(m.at).toLocaleString()}
            </div>
            <div className="whitespace-pre-wrap leading-relaxed">{m.body}</div>
          </li>
        ))}
      </ul>

      {!isClosed && (
        <form onSubmit={sendReply} className="space-y-2 pt-1">
          <textarea
            value={reply}
            onChange={e => setReply(e.target.value)}
            placeholder="Reply to support…"
            rows={3}
            data-testid="help-ticket-reply"
            className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/10"
          />
          {error && <div className="text-rose-700 text-[11px]">{error}</div>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={closeTicket}
              disabled={closing}
              data-testid="help-ticket-close"
              className="h-9 px-4 rounded-full border border-border hover:bg-secondary text-xs"
            >
              {closing ? "Closing…" : "Close ticket"}
            </button>
            <button
              type="submit"
              disabled={sending || !reply.trim()}
              data-testid="help-ticket-reply-send"
              className="flex-1 h-9 rounded-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60 text-xs font-medium inline-flex items-center justify-center gap-2"
            >
              {sending ? <Loader2 className="w-3 h-3 animate-spin"/> : <Send className="w-3 h-3"/>}
              {sending ? "Sending…" : "Send reply"}
            </button>
          </div>
        </form>
      )}
      {isClosed && (
        <div className="text-[11px] text-foreground/55 text-center pt-1">
          This ticket is closed. Open a new one if you need more help.
        </div>
      )}
    </div>
  );
}


/* ─── Bug / Feature quick-compose tabs ──────────────────────────────────── */
function QuickComposeTab({ lockedType, prefill, onPrefillConsumed, onCreated }) {
  const isBug = lockedType === "bug";
  return (
    <div className="space-y-3" data-testid={`help-quick-${lockedType}`}>
      <div
        className={`rounded-xl border px-3 py-2.5 text-[11.5px] leading-relaxed flex items-start gap-2 ${
          isBug
            ? "border-rose-200 bg-rose-50 text-rose-900"
            : "border-amber-200 bg-amber-50 text-amber-900"
        }`}
      >
        {isBug ? <Bug className="w-3.5 h-3.5 mt-0.5 shrink-0"/> : <Lightbulb className="w-3.5 h-3.5 mt-0.5 shrink-0"/>}
        <span>
          {isBug
            ? "Tell us what broke. Include the URL, the order or invoice number, and what you expected to happen."
            : "Have an idea that would make verqo better? Describe the use-case and the outcome you'd love. Other users can upvote it on the Roadmap."}
        </span>
      </div>
      <NewTicketForm
        lockedType={lockedType}
        prefill={prefill}
        onPrefillConsumed={onPrefillConsumed}
        onCancel={null}
        onCreated={onCreated}
      />
    </div>
  );
}

/* ─── Roadmap preview tab ───────────────────────────────────────────────── */
const ROADMAP_COLS = [
  { key: "in_progress", label: "In progress", chip: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "planned",     label: "Planned",     chip: "bg-blue-50 text-blue-700 border-blue-200" },
  { key: "shipped",     label: "Shipped",     chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
];

function RoadmapTab({ onCompose }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/roadmap")
      .then(r => setData(r.data || {}))
      .catch(() => setData({}))
      .finally(() => setLoading(false));
  }, []);

  const totalCount = useMemo(() => {
    if (!data) return 0;
    return Object.values(data).reduce((s, arr) => s + (Array.isArray(arr) ? arr.length : 0), 0);
  }, [data]);

  const toggleVote = async (item) => {
    setVoting(item.id);
    const wasVoted = item.voted_by_me;
    setData(d => {
      if (!d) return d;
      const next = { ...d };
      for (const k of Object.keys(next)) {
        next[k] = (next[k] || []).map(r =>
          r.id === item.id
            ? { ...r, voted_by_me: !wasVoted, votes_count: (r.votes_count || 0) + (wasVoted ? -1 : 1) }
            : r
        );
      }
      return next;
    });
    try {
      if (wasVoted) await api.delete(`/roadmap/${item.id}/upvote`);
      else          await api.post(`/roadmap/${item.id}/upvote`);
    } catch {
      /* refetch on error */
      const r = await api.get("/roadmap").catch(() => ({ data: {} }));
      setData(r.data || {});
    } finally {
      setVoting(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="help-roadmap-tab">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11.5px] text-foreground/65 leading-relaxed">
          Vote on what we build next — the most-upvoted ideas get prioritised.
        </div>
        <button
          type="button"
          onClick={() => navigate("/roadmap")}
          data-testid="help-roadmap-open-full"
          className="text-[11px] mono uppercase tracking-[0.14em] text-foreground/65 hover:text-foreground inline-flex items-center gap-1 shrink-0"
        >
          Open full <ArrowRight className="w-3 h-3"/>
        </button>
      </div>

      {loading ? (
        <div className="text-center py-6 text-foreground/55 text-xs inline-flex items-center justify-center gap-2 w-full">
          <Loader2 className="w-3.5 h-3.5 animate-spin"/>Loading roadmap…
        </div>
      ) : totalCount === 0 ? (
        <div className="text-center py-6">
          <Map className="w-7 h-7 mx-auto text-foreground/30 mb-2"/>
          <div className="text-[12.5px] text-foreground/65">No suggestions yet — be the first.</div>
          <button
            onClick={onCompose}
            data-testid="help-roadmap-suggest"
            className="mt-3 inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-foreground text-background hover:bg-foreground/90 text-xs font-medium"
          >
            <Lightbulb className="w-3.5 h-3.5"/>Suggest a feature
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {ROADMAP_COLS.map(col => {
            const items = (data?.[col.key] || []).slice(0, 3);
            if (items.length === 0) return null;
            return (
              <div key={col.key} data-testid={`help-roadmap-col-${col.key}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`text-[10px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${col.chip}`}>
                    {col.label}
                  </span>
                  <span className="text-[10px] mono text-foreground/40">{(data?.[col.key] || []).length}</span>
                </div>
                <ul className="space-y-1.5">
                  {items.map(item => {
                    const shipped = col.key === "shipped";
                    const isVoting = voting === item.id;
                    return (
                      <li key={item.id} className="rounded-xl border border-border bg-white p-2.5 flex items-start gap-2.5"
                          data-testid={`help-roadmap-item-${item.id}`}>
                        {shipped ? (
                          <div className="w-8 shrink-0 grid place-items-center pt-0.5">
                            <Check className="w-4 h-4 text-emerald-600"/>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => toggleVote(item)}
                            disabled={isVoting}
                            data-testid={`help-roadmap-vote-${item.id}`}
                            className={`w-8 shrink-0 rounded-md border flex flex-col items-center justify-center py-1 transition-colors ${
                              item.voted_by_me
                                ? "bg-foreground border-foreground text-background"
                                : "bg-white border-border text-foreground/65 hover:border-foreground/40"
                            } ${isVoting ? "opacity-50" : ""}`}
                          >
                            <ArrowUp className="w-3 h-3"/>
                            <span className="text-[10px] mono font-semibold mt-0.5">{item.votes_count || 0}</span>
                          </button>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-[12px] font-medium leading-snug">{item.subject}</div>
                          <div className="mt-0.5 text-[10px] mono uppercase tracking-[0.14em] text-foreground/45">
                            #{item.number}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
          <button
            onClick={onCompose}
            data-testid="help-roadmap-suggest"
            className="w-full h-9 rounded-full border border-border hover:bg-secondary text-xs font-medium inline-flex items-center justify-center gap-1.5"
          >
            <Lightbulb className="w-3.5 h-3.5"/>Suggest a new feature
          </button>
        </div>
      )}
    </div>
  );
}


/* ─── "I'm stuck" tab — screenshot + context capture ──────────────────── */
function StuckTab({ onCreated }) {
  const [description, setDescription] = useState("");
  const [screenshot, setScreenshot] = useState(null);  // dataURL
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);
  const { user } = useAuth();

  // Build auto-captured context (URL, viewport, user agent, recent clicks).
  const buildContext = () => {
    const ctx = {
      current_url: typeof window !== "undefined" ? window.location.href : "",
      user_agent:  typeof navigator !== "undefined" ? navigator.userAgent : "",
      viewport:    typeof window !== "undefined" ? `${window.innerWidth}x${window.innerHeight}` : "",
      signup_step: deriveSignupStep(user),
      last_actions: [],
    };
    // Pull recent actions stashed in sessionStorage by app-wide event listener.
    try {
      const raw = sessionStorage.getItem("verqo_last_actions") || "[]";
      ctx.last_actions = JSON.parse(raw).slice(-10);
    } catch { /* ignore */ }
    return ctx;
  };

  const captureScreenshot = async () => {
    setCapturing(true);
    setError(null);
    try {
      const html2canvas = (await import("html2canvas")).default;
      // Hide the help popover during capture so the screenshot is of the
      // page underneath, not the help panel itself.
      const popover = document.querySelector('[data-testid="help-support-popover"]');
      const backdrop = document.querySelector('[data-testid="help-support-backdrop"]');
      const prev = { popover: popover?.style.visibility, backdrop: backdrop?.style.visibility };
      if (popover)  popover.style.visibility = "hidden";
      if (backdrop) backdrop.style.visibility = "hidden";
      await new Promise(r => setTimeout(r, 50));
      const canvas = await html2canvas(document.body, {
        scale: Math.min(1, (window.devicePixelRatio || 1) / 2),
        logging: false,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      if (popover)  popover.style.visibility = prev.popover || "";
      if (backdrop) backdrop.style.visibility = prev.backdrop || "";
      // Compress to JPEG ~0.7 quality so the base64 payload stays small.
      const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
      setScreenshot(dataUrl);
    } catch (e) {
      setError("Couldn't capture screenshot: " + (e?.message || "unknown error"));
    } finally {
      setCapturing(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!description.trim() || description.trim().length < 3) {
      setError("Please describe where you're stuck (min 3 chars).");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const ctx = buildContext();
      // Strip the data URL prefix — backend handles either format.
      const sb64 = screenshot ? screenshot.split(",", 2)[1] : null;
      const r = await api.post("/help/stuck", {
        description: description.trim(),
        current_url: ctx.current_url,
        user_agent:  ctx.user_agent,
        viewport:    ctx.viewport,
        signup_step: ctx.signup_step,
        last_actions: ctx.last_actions,
        screenshot_b64: sb64,
        urgency: "high",
      });
      setCreated(r.data);
      setTimeout(() => onCreated?.(), 1800);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (created) {
    return (
      <div className="text-center py-6" data-testid="help-stuck-submitted">
        <div className="w-12 h-12 rounded-full bg-emerald-100 grid place-items-center mx-auto">
          <Check className="w-5 h-5 text-emerald-700"/>
        </div>
        <div className="font-medium mt-3">Help is on the way</div>
        <div className="text-xs text-foreground/55 mt-1">
          We've opened ticket <span className="mono">#{created.ticket_number}</span> with your context attached.
          An admin will reach out shortly.
        </div>
      </div>
    );
  }

  const ctx = buildContext();
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="help-stuck-tab">
      <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-900 px-3 py-2.5 text-[11.5px] leading-relaxed flex items-start gap-2">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
        <span>
          Tell us where you're stuck. We'll auto-attach your current screen,
          URL, and browser so admin can help immediately — no need to repeat
          context.
        </span>
      </div>
      <textarea
        value={description}
        onChange={e => setDescription(e.target.value)}
        placeholder="What screen are you on? What were you trying to do? What's not working?"
        rows={4}
        required
        minLength={3}
        maxLength={4000}
        data-testid="help-stuck-description"
        className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/10"
      />

      {/* Screenshot capture */}
      <div className="rounded-xl border border-border bg-secondary/30 p-3 space-y-2.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-medium inline-flex items-center gap-1.5">
            <ImageIcon className="w-3.5 h-3.5 text-foreground/55"/>
            Screenshot
            <span className="text-[10px] text-foreground/45 font-normal">· optional</span>
          </span>
          {screenshot ? (
            <button
              type="button"
              onClick={() => setScreenshot(null)}
              data-testid="help-stuck-screenshot-clear"
              className="text-[11px] text-foreground/60 hover:text-foreground inline-flex items-center gap-1"
            >
              <X className="w-3 h-3"/>Remove
            </button>
          ) : null}
        </div>
        {screenshot ? (
          <div className="rounded-lg border border-border bg-white overflow-hidden">
            <img src={screenshot} alt="Captured" className="w-full h-auto max-h-[200px] object-contain"
                 data-testid="help-stuck-screenshot-preview"/>
          </div>
        ) : (
          <button
            type="button"
            onClick={captureScreenshot}
            disabled={capturing}
            data-testid="help-stuck-screenshot-btn"
            className="w-full h-9 rounded-full border border-dashed border-border hover:bg-secondary/50 text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
          >
            {capturing ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Camera className="w-3.5 h-3.5"/>}
            {capturing ? "Capturing this screen…" : "Capture this screen"}
          </button>
        )}
      </div>

      {/* Context preview */}
      <details className="text-[11px] text-foreground/55">
        <summary className="cursor-pointer hover:text-foreground select-none" data-testid="help-stuck-context-toggle">
          What we'll attach automatically
        </summary>
        <div className="mt-2 rounded-lg bg-secondary/30 px-3 py-2 space-y-0.5 font-mono text-[10.5px]" data-testid="help-stuck-context">
          <div><span className="text-foreground/45">URL:</span> {ctx.current_url || "—"}</div>
          <div><span className="text-foreground/45">Viewport:</span> {ctx.viewport || "—"}</div>
          {ctx.signup_step && <div><span className="text-foreground/45">Step:</span> {ctx.signup_step}</div>}
          <div><span className="text-foreground/45">UA:</span> {(ctx.user_agent || "").slice(0, 70)}…</div>
        </div>
      </details>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-800 text-[12px] p-2.5 inline-flex items-start gap-2 w-full">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
          <span>{error}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting || !description.trim()}
        data-testid="help-stuck-submit"
        className="w-full h-10 rounded-full bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-60 text-sm font-medium inline-flex items-center justify-center gap-2"
      >
        {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>}
        {submitting ? "Sending…" : "Get help now"}
      </button>
    </form>
  );
}

// Helper — figure out which signup step the user is on, if relevant.
function deriveSignupStep(user) {
  if (!user) return null;
  if (!user.email_verified) return "email_verification";
  const kyc = user.kyc_status || user.kyb_status;
  if (kyc && !["approved", "passed", "verified"].includes(kyc)) return "kyb_verification";
  if (!user.company || !user.ein) return "business_details";
  return null;
}
